import { useMemo } from 'react';

import { useMemberId } from '@trello/authentication';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { getDateBefore, idToDate } from '@trello/dates';
import { useFeatureGate } from '@trello/feature-gate-client';

import { usePersonalProductivityCohortValue } from 'app/src/components/PersonalProductivityBeta';
import { useMemberNodeIdFragment } from 'app/src/components/Planner/MemberNodeIdFragment.generated';
import { useMemberCampaignsFragment } from './MemberCampaignsFragment.generated';
import { useTrelloPlannerEnabledCalendarsQuery } from './TrelloPlannerEnabledCalendarsQuery.generated';

export const useIsEligibleForOutlookAnnouncement = () => {
  const memberId = useMemberId();

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();
  const hasDismissedAnnouncement = isOneTimeMessageDismissed(
    'outlook-announcement-spotlight',
  );

  const { value: isGateEnabled } = useFeatureGate(
    'trello_outlook_announcement',
  );

  const personalProductivityCohort = usePersonalProductivityCohortValue();
  const isGA = personalProductivityCohort === 'ga';

  const { data: memberNodeId } = useMemberNodeIdFragment({
    from: { id: memberId },
    optimistic: true,
  });

  const { data: plannerAccountData, loading } =
    useTrelloPlannerEnabledCalendarsQuery({
      variables: { nodeId: memberNodeId?.nodeId || '' },
      skip:
        !isGateEnabled ||
        !memberNodeId?.nodeId ||
        !isGA ||
        hasDismissedAnnouncement,
      waitOn: ['MemberHeader'],
    });

  const { data: memberCampaigns } = useMemberCampaignsFragment({
    from: { id: memberId },
  });

  const isEligible = useMemo(() => {
    const oneDayBeforePlannerDiscoveryReleaseDate = new Date('07/13/2025');
    const isAccountAgeEligible =
      idToDate(memberId) < oneDayBeforePlannerDiscoveryReleaseDate;

    const hasNotConnectedCalendar =
      !loading &&
      plannerAccountData?.trello?.member?.planner?.accounts?.edges?.length ===
        0;

    /**
     * The user could be a direct signup before the splitscreen campaign was
     * released, but for the needs of identifying users invited within the
     * last two weeks, using the splitscreen campaign to identify direct
     * signup is accurate.
     */
    const isDirectSignup = Boolean(
      (memberCampaigns?.campaigns ?? []).find(
        (c: { name: string }) => c.name === 'splitscreen',
      )?.dateDismissed,
    );
    const canShowOutlookAnnouncementToAnInvitedUser =
      idToDate(memberId) <= getDateBefore({ days: 14 });

    return (
      isGateEnabled &&
      isAccountAgeEligible &&
      isGA &&
      !hasDismissedAnnouncement &&
      hasNotConnectedCalendar &&
      (isDirectSignup || canShowOutlookAnnouncementToAnInvitedUser)
    );
  }, [
    hasDismissedAnnouncement,
    isGA,
    isGateEnabled,
    loading,
    memberCampaigns,
    memberId,
    plannerAccountData,
  ]);

  return { isEligible };
};
